import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { AlertTriangle, Users, Sun, Compass, FileText, ImageIcon, CalendarClock, ArrowRight, Trees } from "lucide-react";

// TODAY · NEOS operational command surface (Lean-Core Pass 2 · 2B).
// 7 rails aggregated from existing collections via GET /api/today.
// No parallel data universe; explicit UNKNOWN states where information is absent.
export default function Dashboard() {
  const [today, setToday] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/today").then(r => setToday(r.data)).catch(e => setErr(e?.response?.data?.detail || e.message));
  }, []);

  if (err) return <div className="p-8 text-red-700" data-testid="today-error">TODAY failed to load: {err}</div>;
  if (!today) return <div className="p-8 text-forest/60" data-testid="today-loading">Loading TODAY…</div>;

  const r = today.rails;
  const oppSummary = `${r.opportunities.verdict_go} GO · ${r.opportunities.verdict_review} REVIEW · ${r.opportunities.verdict_no_go} NO_GO`;

  return (
    <div className="space-y-6" data-testid="today-command-surface">
      <header>
        <div className="overline">Command Surface</div>
        <h1 className="font-serif text-4xl mt-1">Today</h1>
        <p className="text-ink-soft">{dayjs().format("dddd, MMMM D, YYYY")} · <span className="text-xs">generated {dayjs(today.generated_at).format("HH:mm")} UTC</span></p>
      </header>

      {/* Rail 1 — Founder decisions (only real authority items) */}
      <Rail title="Founder Decisions" icon={AlertTriangle} accent="terracotta"
            subtitle={`${r.founder_decisions.pending} pending in founder_review_queue`}
            action={{ to: "/admin/discovery", label: "Open Review Queue" }}
            testid="rail-founder-decisions">
        {r.founder_decisions.recent.length === 0
          ? <Empty label="Nothing requires Founder authority right now." />
          : <ul className="space-y-2" data-testid="rail-founder-decisions-list">
              {r.founder_decisions.recent.map((f, i) => (
                <li key={f.id || i} className="flex items-start gap-3 text-sm py-1">
                  <span className="text-xs uppercase tracking-wider text-terracotta w-24 shrink-0">{f.kind || f.status || "pending"}</span>
                  <span className="flex-1 truncate">{f.title || f.summary || f.opportunity_id || f.id}</span>
                  <span className="text-xs text-ink-soft">{f.created_at ? dayjs(f.created_at).format("MMM D") : ""}</span>
                </li>
              ))}
            </ul>}
      </Rail>

      {/* Rail 2 — Opportunities (real DB counts + REVIEW top 5) */}
      <Rail title="Opportunities" icon={Compass} accent="forest"
            subtitle={oppSummary + ` · ${r.opportunities.qualified_total} qualified total`}
            action={{ to: "/admin/discovery", label: "Work REVIEW queue" }}
            testid="rail-opportunities">
        <div className="text-xs text-ink-soft italic mb-3" data-testid="opportunities-note">
          {r.opportunities.note}
        </div>
        {r.opportunities.review_top.length === 0
          ? <Empty label="No REVIEW opportunities in the queue." />
          : <ul className="space-y-1.5" data-testid="rail-opportunities-list">
              {r.opportunities.review_top.map(o => (
                <li key={o.id} className="flex items-center gap-3 text-sm py-1 border-b border-forest/5 last:border-0">
                  <Link to={`/admin/discovery?opp=${o.id}`} className="flex-1 truncate hover:underline">{o.title || "(untitled)"}</Link>
                  <span className="text-xs text-ink-soft truncate max-w-[180px]">{o.funder || ""}</span>
                  <span className="text-xs text-forest/70">{o.deadline || "no deadline"}</span>
                </li>
              ))}
            </ul>}
      </Rail>

      {/* Rail 3 — Sanctuary (honest: NOT_YET_CONNECTED) */}
      <Rail title="Sanctuary" icon={Trees} accent="forest"
            subtitle={r.sanctuary.state} testid="rail-sanctuary">
        <div className="text-sm text-ink-soft mb-3" data-testid="sanctuary-note">{r.sanctuary.note}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="sanctuary-domains">
          {r.sanctuary.domains.map(d => (
            <div key={d.name} className="text-xs bg-bone/40 px-2 py-1.5 rounded border border-line flex items-center justify-between">
              <span className="capitalize">{d.name.replace(/_/g, " ")}</span>
              <span className="text-ink-soft">{d.state}</span>
            </div>
          ))}
        </div>
      </Rail>

      {/* Rail 4 — People (real counts + recent 8) */}
      <Rail title="People" icon={Users} accent="forest"
            subtitle={`Contacts ${r.people.counts.contacts} · Volunteers ${r.people.counts.volunteer_applications} · Retreat ${r.people.counts.retreat_applications} · Partnerships ${r.people.counts.partnerships}`}
            action={{ to: "/admin/crm", label: "Open People" }}
            testid="rail-people">
        {r.people.recent.length === 0
          ? <Empty label="No recent relationship activity." />
          : <ul className="space-y-1 text-sm" data-testid="rail-people-list">
              {r.people.recent.map((p, i) => (
                <li key={`${p.source}-${p.id || i}`} className="flex items-center gap-3 py-1">
                  <span className="text-[10px] uppercase tracking-wider text-forest/60 w-20 shrink-0">{p.source.replace(/_/g, " ")}</span>
                  <span className="flex-1 truncate">{p.name}</span>
                  <span className="text-xs text-ink-soft">{p.created_at ? dayjs(p.created_at).format("MMM D") : ""}</span>
                </li>
              ))}
            </ul>}
      </Rail>

      {/* Rail 5 — Coming Next (curriculum + retreats + grant deadlines) */}
      <Rail title="Coming Next" icon={CalendarClock} accent="terracotta"
            subtitle="Living Calendar + retreat + grant deadlines (next 60 days)"
            action={{ to: "/admin/curriculum", label: "Living Calendar" }}
            testid="rail-coming-next">
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-forest/60 mb-1">Curriculum · Now</div>
            {r.coming_next.curriculum_now
              ? <div className="text-sm" data-testid="coming-next-curriculum">
                  <div className="font-medium">{r.coming_next.curriculum_now.title || `Moon ${r.coming_next.curriculum_now.moon_index}`}</div>
                  <div className="text-xs text-ink-soft">{r.coming_next.curriculum_now.theme || r.coming_next.curriculum_now.element}</div>
                </div>
              : <Empty label="No curriculum cycle." />}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-forest/60 mb-1">Retreats</div>
            {r.coming_next.upcoming_retreats.length === 0
              ? <Empty label="No upcoming retreats." />
              : <ul data-testid="coming-next-retreats">
                  {r.coming_next.upcoming_retreats.map(x => (
                    <li key={x.id} className="text-sm py-0.5 flex gap-2">
                      <span className="text-xs text-forest/70 w-16">{x.start_date}</span>
                      <span className="flex-1 truncate">{x.title}</span>
                    </li>
                  ))}
                </ul>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-forest/60 mb-1">Grant Deadlines</div>
            {r.coming_next.upcoming_grant_deadlines.length === 0
              ? <Empty label="No grant deadlines in the next 60 days." />
              : <ul data-testid="coming-next-grants">
                  {r.coming_next.upcoming_grant_deadlines.map(x => (
                    <li key={x.id} className="text-sm py-0.5 flex gap-2">
                      <span className="text-xs text-forest/70 w-16">{x.deadline}</span>
                      <span className="flex-1 truncate">{x.name}</span>
                    </li>
                  ))}
                </ul>}
          </div>
        </div>
      </Rail>

      {/* Rail 6 — Evidence & Outcomes */}
      <Rail title="Evidence & Outcomes" icon={FileText} accent="forest"
            subtitle={`${r.evidence_outcomes.total_events.toLocaleString()} institutional events · showing 20 most recent`}
            testid="rail-evidence">
        {r.evidence_outcomes.recent.length === 0
          ? <Empty label="No recent institutional events." />
          : <ul className="space-y-1 text-sm max-h-56 overflow-y-auto" data-testid="rail-evidence-list">
              {r.evidence_outcomes.recent.map((e, i) => (
                <li key={i} className="flex items-center gap-3 py-0.5 border-b border-forest/5 last:border-0">
                  <span className="text-[10px] font-mono text-forest/60 w-36 shrink-0 truncate">{e.kind}</span>
                  <span className="flex-1 truncate text-xs">{e.summary || e.actor || "—"}</span>
                  <span className="text-[10px] text-ink-soft">{e.created_at ? dayjs(e.created_at).format("MMM D HH:mm") : ""}</span>
                </li>
              ))}
            </ul>}
      </Rail>

      {/* Rail 7 — Story / Public */}
      <Rail title="Story / Media" icon={ImageIcon} accent="terracotta"
            subtitle={`${r.story_public.counts.media_total} media · ${r.story_public.counts.content_blocks} content blocks`}
            action={{ to: "/admin/media", label: "Open Story / Media" }}
            testid="rail-story-public">
        <div className="text-xs italic text-ink-soft mb-3" data-testid="story-workflow-state">{r.story_public.workflow_state}</div>
        {r.story_public.recent_media.length === 0
          ? <Empty label="No media items." />
          : <div className="flex gap-3 flex-wrap" data-testid="rail-story-media">
              {r.story_public.recent_media.map(m => (
                <div key={m.id} className="w-32 text-xs">
                  <div className="h-20 bg-bone/40 rounded border border-line flex items-center justify-center overflow-hidden">
                    {m.storage_path ? <span className="text-[10px] text-forest/60 px-1 text-center truncate">{m.original_filename || m.storage_path.split("/").pop()}</span> : <ImageIcon className="h-6 w-6 text-forest/30" />}
                  </div>
                  <div className="mt-1 truncate">{m.original_filename || m.id}</div>
                </div>
              ))}
            </div>}
      </Rail>
    </div>
  );
}

// ---- Rail primitive --------------------------------------------------------
function Rail({ title, icon: Icon, accent = "forest", subtitle, action, testid, children }) {
  return (
    <section className="bg-admin-surface border border-line rounded-xl overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-line bg-bone/40">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${accent === "terracotta" ? "text-terracotta" : "text-forest"}`} strokeWidth={1.5} />
          <div className="overline">{title}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-ink-soft">{subtitle}</div>
          {action && <Link to={action.to} className="text-xs text-forest hover:underline flex items-center gap-1" data-testid={`${testid}-action`}>{action.label} <ArrowRight className="h-3 w-3" /></Link>}
        </div>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function Empty({ label }) {
  return <div className="text-sm text-ink-soft italic py-2" data-testid="rail-empty">{label}</div>;
}
